import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaArrowRight, FaSearch, FaFilter } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { API_ENDPOINTS } from '../config/api';
import { getImageUrl } from '../utils/imageUtils';
import ImagePlaceholder from './ImagePlaceholder';

const UnifiedPublicationList = ({ isPublic = false, defaultFilter = 'All Types' }) => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(defaultFilter);
  const [searchTerm, setSearchTerm] = useState('');
  const [types, setTypes] = useState([]);

  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    fetchPublications();
    fetchTypes();
  }, []);

  // Update filter when defaultFilter prop changes
  useEffect(() => {
    if (defaultFilter && defaultFilter !== filter) {
      console.log('🔄 Updating filter from prop:', defaultFilter);
      setFilter(defaultFilter);
    }
  }, [defaultFilter]);

  // Refetch publications when filter changes
  useEffect(() => {
    if (isPublic) {
      fetchPublications();
    }
  }, [filter]);

  // Debounced search effect
  useEffect(() => {
    if (isPublic) {
      const timeoutId = setTimeout(() => {
        fetchPublications();
      }, 500); // Wait 500ms after user stops typing
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const fetchPublications = async () => {
    try {
      const endpoint = isPublic 
        ? API_ENDPOINTS.TEST_PUBLICATIONS
        : API_ENDPOINTS.PUBLICATIONS;
      
      // Add query parameters for filtering
      const params = {};
      if (isPublic && filter && filter !== 'All Types') {
        params.type = filter;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      console.log('🔍 Fetching publications with params:', params);
      console.log('🔍 Current filter:', filter);
      
      const response = await axios.get(endpoint, { params });
      const apiPublications = response.data.publications || response.data;
      
      console.log('📚 Received publications:', apiPublications.length);
      console.log('📚 First publication type:', apiPublications[0]?.publication_type_name);
      
      setPublications(apiPublications);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch publications:', err);
      setError(err.response?.data?.error || 'Failed to fetch publications');
      setPublications([]);
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.PUBLICATION_TYPES_PUBLIC);
      setTypes(response.data.map(t => t.name));
    } catch (err) {
      console.error('Failed to fetch publication types:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this publication?')) return;
    
    try {
      await axios.delete(`${API_ENDPOINTS.PUBLICATIONS}/${id}`);
      setPublications(publications.filter(pub => pub.id !== id));
    } catch (err) {
      alert('Error deleting publication: ' + (err.response?.data?.error || err.message));
    }
  };

  const getImageUrlWithFallback = (imagePath, publicationType) => {
    if (imagePath) {
      return getImageUrl(imagePath);
    }
    
    // Return empty string if no image - no fallback to external services
    return '';
  };

  // Helper function to generate the correct publication URL based on type
  const getPublicationUrl = (publication) => {
    if (!publication) return '/publications';
    
    const customUrl = publication.custom_url || publication.id;
    
    // Generate route based on publication type
    switch (publication.publication_type_name) {
              case 'Research Papers':
          return `/research-papers/${customUrl}`;
              case 'Newsletters':
          return `/newsletters/${customUrl}`;
              case 'Blogs':
          return `/blogs/${customUrl}`;
      default:
        return `/publications/${customUrl}`;
    }
  };

  // Use publications directly since filtering is now done at API level
  const filteredPublications = publications;

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
      <div className="text-center">
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading publications...</p>
      </div>
    </div>
  );
  
  if (error) return (
    <div className="container mt-5">
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">❌ Error Loading Publications</h4>
        <p>{error}</p>
      </div>
    </div>
  );

  return (
    <div style={{ 
      background: 'linear-gradient(to right, #91eae4, #86a8e7, #7f7fd5)',
      minHeight: '100vh',
      fontFamily: '"Quicksand", sans-serif',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Navigation - Only for public view */}
      {isPublic && <Header />}

      {/* Main Content Container */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header Section */}
        <div className="container mb-5" style={{ paddingTop: '2rem' }}>
          <div className="row align-items-center">
            <div className={isPublic ? "col-12 text-center" : "col-md-8"}>
              <h1 className="display-6 fw-bold text-white mb-2">
                {isPublic ? 'Explore Our Publications' : 'Publication Management System'}
              </h1>
              <p className="text-white-50 mb-0">
                {isPublic 
                  ? 'Discover insights, research, and industry knowledge from our extensive collection of publications'
                  : 'Discover and manage your organization\'s publications'
                }
              </p>
              
              {/* Search Container - Only for public view */}
              {isPublic && (
                <div style={{
                  maxWidth: '500px',
                  margin: '2rem auto 0',
                  position: 'relative'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
                  }}>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#1f2937" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{ marginRight: '12px' }}
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search publications by title..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        color: '#1f2937',
                        fontSize: '1rem',
                        fontWeight: '400'
                      }}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#6b7280',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Add Publication Button - Only for authenticated users */}
            {!isPublic && (
              <div className="col-md-4 text-end">
                <button
                  className="btn btn-light btn-lg d-inline-flex align-items-center gap-2" 
                  onClick={() => navigate('/dashboard/add-publication')}
                  style={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                  }}
                >
                  <FaPlus /> New Publication
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filter Section */}
        <div className="container mb-4">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="fw-semibold text-white">Filter by type:</span>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    style={{
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      border: 'none',
                      background: filter === 'All Types' ? '#4a5568' : '#ffffff',
                      color: filter === 'All Types' ? '#ffffff' : '#333',
                      boxShadow: filter === 'All Types' 
                        ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.15)',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      if (filter !== 'All Types') {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (filter !== 'All Types') {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onClick={() => navigate('/publications')}
                  >
                    All Types
                  </button>
                  {types.map(type => {
                    const colors = getTypeColors(type);
                    const isSelected = filter === type;
                    const getRouteForType = (typeName) => {
                      switch (typeName) {
                        case 'Research Papers':
                          return '/research-papers';
                        case 'Newsletters':
                          return '/newsletters';
                                case 'Blogs':
          return '/blogs';
                        default:
                          return '/publications';
                      }
                    };
                    return (
                      <button
                        key={type}
                        style={{
                          borderRadius: '20px',
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease',
                          border: 'none',
                          background: isSelected ? colors.bg : '#ffffff',
                          color: isSelected ? colors.text : colors.bg,
                          boxShadow: isSelected 
                            ? '0 4px 12px rgba(0, 0, 0, 0.3)' 
                            : '0 2px 8px rgba(0, 0, 0, 0.15)',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                          }
                        }}
                        onClick={() => navigate(getRouteForType(type))}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <span className="text-white-50 fw-medium">
                Showing {filteredPublications.length} publication{filteredPublications.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        
        {/* Publications Grid */}
        <div className="container">
          {/* Mobile View - Single Column */}
          {isPublic && (
            <div className="d-md-none" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
              width: '100%',
              padding: '2rem 0'
            }}>
              {filteredPublications.map((pub, index) => {
                const imageUrl = getImageUrlWithFallback(pub.image_path, pub.publication_type_name);
                
                return (
                  <div 
                    key={pub.id}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                      overflow: 'hidden',
                      boxShadow: '0 .1rem 1rem rgba(0, 0, 0, 0.1)',
                      borderRadius: '1em',
                      background: 'linear-gradient(to right, #FFFFFF, #ECE9E6)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      const card = e.currentTarget;
                      card.style.transform = 'translateY(-8px)';
                      card.style.boxShadow = '0 1rem 2rem rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      const card = e.currentTarget;
                      card.style.transform = 'translateY(0)';
                      card.style.boxShadow = '0 .1rem 1rem rgba(0, 0, 0, 0.1)';
                    }}
                    onClick={() => {
                      const path = getPublicationUrl(pub);
                      navigate(path);
                    }}
                  >
                    {/* Card Header - Image */}
                    <div style={{ position: 'relative' }}>
                      {imageUrl ? (
                        <img 
                          src={imageUrl}
                          alt={pub.title}
                          style={{
                            width: '100%',
                            height: '280px',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <ImagePlaceholder 
                          height="280px"
                          text="📄 No Image Available"
                        />
                      )}
                      
                      {/* Type Badge */}
                      <span 
                        style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                          padding: '.25em .75em',
                          borderRadius: '1em',
                          fontSize: '.75rem',
                          fontWeight: '600',
                          background: getTypeGradient(pub.publication_type_name),
                          color: '#fafafa',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                      >
                        {pub.publication_type_name}
                      </span>
                    </div>

                    {/* Card Body */}
                    <div style={{ 
                      padding: '1.5rem', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '1.5rem',
                      flex: 1 
                    }}>
                      <h4 style={{ 
                        fontSize: '1.5rem', 
                        textTransform: 'capitalize',
                        fontWeight: '600',
                        color: '#333',
                        margin: 0,
                        lineHeight: '1.3'
                      }}>
                        {pub.title}
                      </h4>
                      
                      {pub.subtitle && (
                        <p style={{
                          color: '#666',
                          fontSize: '0.9rem',
                          lineHeight: '1.6',
                          margin: 0,
                          flex: 1
                        }}>
                          {pub.subtitle}
                        </p>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div style={{ 
                      display: 'flex', 
                      padding: '1.5rem', 
                      marginTop: 'auto',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}>
                      <a 
                        href={getPublicationUrl(pub)}
                        style={{ 
                          color: '#7f7fd5',
                          fontSize: '14px',
                          textDecoration: 'underline',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#5a5a9e';
                          e.target.style.transform = 'translateX(2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#7f7fd5';
                          e.target.style.transform = 'translateX(0)';
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Read full article <FaArrowRight style={{ fontSize: '12px' }} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Desktop View - Grid Layout */}
          <div style={{
            display: isPublic ? 'grid' : 'flex',
            gridTemplateColumns: isPublic ? 'repeat(3, 1fr)' : 'wrap',
            flexWrap: isPublic ? 'unset' : 'wrap',
            width: '100%',
            gap: '2rem',
            justifyContent: 'center',
            padding: '2rem 0'
          }}
          className={isPublic ? 'd-none d-md-grid' : ''}>
            {filteredPublications.map((pub, index) => {
              const imageUrl = getImageUrlWithFallback(pub.image_path, pub.publication_type_name);
              
              return (
                <div 
                  key={pub.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: isPublic ? '100%' : 'clamp(20rem, calc(20rem + 2vw), 22rem)',
                    overflow: 'hidden',
                    boxShadow: '0 .1rem 1rem rgba(0, 0, 0, 0.1)',
                    borderRadius: '1em',
                    background: 'linear-gradient(to right, #FFFFFF, #ECE9E6)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    const card = e.currentTarget;
                    if (isPublic) {
                      card.style.transform = 'translateY(-12px) scale(1.02)';
                      card.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.3)';
                    } else {
                      card.style.transform = 'translateY(-8px)';
                      card.style.boxShadow = '0 1rem 2rem rgba(0, 0, 0, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const card = e.currentTarget;
                    card.style.transform = 'translateY(0)';
                    card.style.boxShadow = '0 .1rem 1rem rgba(0, 0, 0, 0.1)';
                  }}
                  onClick={() => {
                    const path = isPublic 
                      ? getPublicationUrl(pub)
                      : `/dashboard/publications/${pub.custom_url || pub.id}`;
                    navigate(path);
                  }}
                >
                  {/* Card Header - Image */}
                  <div style={{ position: 'relative' }}>
                    {imageUrl ? (
                      <img 
                        src={imageUrl}
                        alt={pub.title}
                        style={{
                          width: '100%',
                          height: isPublic ? '280px' : '200px',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onError={(e) => {
                          // Remove broken image - no fallback to external services
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <ImagePlaceholder 
                        height={isPublic ? '280px' : '200px'}
                        text="📄 No Image Available"
                      />
                    )}
                    
                    {/* Type Badge */}
                    <span 
                      style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        padding: '.25em .75em',
                        borderRadius: '1em',
                        fontSize: '.75rem',
                        fontWeight: '600',
                        background: getTypeGradient(pub.publication_type_name),
                        color: '#fafafa',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {pub.publication_type_name}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div style={{ 
                    padding: isPublic ? '1.5rem' : '1rem', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: isPublic ? '1.5rem' : '0.5rem',
                    flex: 1 
                  }}>
                    <h4 style={{ 
                      fontSize: '1.5rem', 
                      textTransform: 'capitalize',
                      fontWeight: '600',
                      color: '#333',
                      margin: 0,
                      lineHeight: '1.3'
                    }}>
                      {pub.title}
                    </h4>
                    
                    {pub.subtitle && (
                      <p style={{
                        color: '#666',
                        fontSize: '0.9rem',
                        lineHeight: isPublic ? '1.6' : '1.5',
                        margin: 0,
                        flex: 1
                      }}>
                        {pub.subtitle}
                      </p>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div style={{ 
                    display: 'flex', 
                    padding: isPublic ? '1.5rem' : '1rem', 
                    marginTop: 'auto',
                    justifyContent: isPublic ? 'flex-start' : 'space-between',
                    alignItems: 'center'
                  }}>
                    <button 
                      className="btn btn-link text-decoration-none p-0 fw-semibold"
                      style={{
                        color: '#7f7fd5',
                        fontSize: '14px',
                        border: 'none',
                        background: 'none',
                        padding: 0
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const path = isPublic 
                          ? `/publications/${pub.custom_url || pub.id}`
                          : `/dashboard/publications/${pub.custom_url || pub.id}`;
                        navigate(path);
                      }}
                    >
                      {isPublic ? (
                        <a 
                          href={getPublicationUrl(pub)}
                          style={{ 
                            color: '#7f7fd5',
                            fontSize: '14px',
                            textDecoration: 'underline',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#5a5a9e';
                            e.target.style.transform = 'translateX(2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#7f7fd5';
                            e.target.style.transform = 'translateX(0)';
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Read full article <FaArrowRight style={{ fontSize: '12px' }} />
                        </a>
                      ) : (
                        <>
                          Read full article <FaArrowRight style={{ fontSize: '12px', marginLeft: '4px' }} />
                        </>
                      )}
                    </button>
                    
                    {/* Admin Actions - Only for authenticated users */}
                    {!isPublic && isAdmin && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          style={{
                            borderRadius: '8px', 
                            padding: '6px 12px',
                            fontSize: '12px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/publications/${pub.custom_url || pub.id}/edit`);
                          }}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          style={{
                            borderRadius: '8px', 
                            padding: '6px 12px',
                            fontSize: '12px'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(pub.id);
                          }}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* No Publications Found */}
          {filteredPublications.length === 0 && (
            <div className="text-center py-5">
              <div style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>📭</div>
              <h3 className="text-white mb-2">No Publications Found</h3>
              <p className="text-white-50">
                {isPublic 
                  ? 'Try adjusting your filters or check back later for new publications.'
                  : 'Try adjusting your filters or create a new publication to get started.'
                }
              </p>
              {!isPublic && (
                <button 
                  className="btn btn-light"
                  onClick={() => navigate('/dashboard/add-publication')}
                >
                  Create Your First Publication
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Footer - Only for public view */}
      {isPublic && <Footer />}
    </div>
  );
};

// Helper functions
function getTypeGradient(type) {
  switch (type) {
            case 'Newsletters': return 'linear-gradient(to bottom, #2F80ED, #56CCF2)';
    case 'Technical Report': return 'linear-gradient(to bottom, #11998e, #38ef7d)';
            case 'Research Papers': return 'linear-gradient(to bottom, #667eea, #764ba2)';
    case 'Policy Document': return 'linear-gradient(to bottom, #f093fb, #f5576c)';
    case 'Annual Report': return 'linear-gradient(to bottom, #4facfe, #00f2fe)';
    case 'White Paper': return 'linear-gradient(to bottom, #ff6b6b, #ee5a24)';
    case 'Bulletin': return 'linear-gradient(to bottom, #fa709a, #fee140)';
    default: return 'linear-gradient(to bottom, #667eea, #764ba2)';
  }
}

function getTypeColors(type) {
  switch (type) {
            case 'Newsletters': return { bg: '#2F80ED', text: '#ffffff', border: '#56CCF2' };
    case 'Technical Report': return { bg: '#11998e', text: '#ffffff', border: '#38ef7d' };
            case 'Research Papers': return { bg: '#667eea', text: '#ffffff', border: '#764ba2' };
    case 'Policy Document': return { bg: '#f093fb', text: '#ffffff', border: '#f5576c' };
    case 'Annual Report': return { bg: '#4facfe', text: '#ffffff', border: '#00f2fe' };
    case 'White Paper': return { bg: '#ff6b6b', text: '#ffffff', border: '#ee5a24' };
    case 'Bulletin': return { bg: '#fa709a', text: '#ffffff', border: '#fee140' };
    default: return { bg: '#667eea', text: '#ffffff', border: '#764ba2' };
  }
}



export default UnifiedPublicationList; 